import { type IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import type { IGeoObject, TCustomBalloonFetchHtmlFunction } from '@cian/frontend-newbuilding-map-component/es/types';

import { type FC, useCallback, useEffect, useState } from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { useSelector } from 'react-redux';

import { type RoomsInfoItem } from 'shared/components/RoomsList';
import { getLocationCenterCoordinates } from 'shared/selectors/coordinates';
import { getFilteredResidentialComplexes } from 'shared/selectors/residentialComplexes/getFilteredResidentialComplexes';
import { getResidentialComplexes } from 'shared/selectors/residentialComplexes/getResidentialComplexes';
import { newbuildingsPlusBlocksPikSelector } from 'shared/selectors/specialPromosBlocks/newbuildingsPlusBlocksPikSelector';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { extractUndefined } from 'shared/utils/extractUndefined';

import { Map } from './Map';
import { MapBalloon } from './internal/components/MapBalloon';
import { trackMapBalloonClose } from './internal/tracking/trackMapBalloonClose';
import { trackMapBalloonCtaClick } from './internal/tracking/trackMapBalloonCtaClick';
import { trackMapBalloonOpen } from './internal/tracking/trackMapBalloonOpen';
import { trackMapPinClick } from './internal/tracking/trackMapPinClick';
import { getPins } from './utils/getPins';

const defaultRoomsInfo = Array.of<RoomsInfoItem>();

export const MapContainer: FC = () => {
  const { config } = useApplicationContext();
  const { hash, list } = useSelector(getResidentialComplexes);
  const [pinTypes, setPinTypes] = useState<string[]>([]);
  const filteredComplexes = useSelector(getFilteredResidentialComplexes);
  const { center, zoom } = useSelector(getLocationCenterCoordinates);
  const newbuildingsPlusBlocksPik = useSelector(newbuildingsPlusBlocksPikSelector);

  const mapApiKey = config.get<string>('builders.YandexApiKey') || '';

  const handleGeoObjectsFirstFetch = (): Promise<IMapGeoObject[]> => {
    const pins = getPins(list);

    return Promise.resolve(pins);
  };

  const handleGeoObjectClick = useCallback((object: IGeoObject) => {
    trackMapPinClick(object.options.id);
  }, []);

  const handleBalloonOpen = useCallback((object: IGeoObject) => {
    trackMapBalloonOpen(object.options.id);
  }, []);

  const handleBalloonClose = useCallback((object: IGeoObject) => {
    trackMapBalloonClose(object.options.id);
  }, []);

  const handleBalloonHtmlFirstFetch: TCustomBalloonFetchHtmlFunction = geoObject => {
    const numberedId = Number(geoObject.options.id);
    const complex = hash[numberedId];

    if (!complex) {
      return { html: Promise.reject(new Error('Не верный тип пина!')) };
    }

    const roomsInfo = complex.roomsInfo || defaultRoomsInfo;
    const subtitle = extractUndefined(complex.finishInfo?.label);
    const promoLabel = extractUndefined(complex.promoLabel);
    const imageUrl = complex.imgUrl || complex.fullImgUrl || '';

    const isSeriaPlus =
      !!newbuildingsPlusBlocksPik.length && newbuildingsPlusBlocksPik.some(pik => pik.newbuildingId === complex.id);

    return {
      closeSelector: '#close',
      html: Promise.resolve(
        ReactDOMServer.renderToStaticMarkup(
          <MapBalloon
            imageUrl={imageUrl}
            isSeriaPlus={isSeriaPlus}
            newbuildingId={complex.id}
            promoLabel={promoLabel}
            roomsInfo={roomsInfo}
            subtitle={subtitle}
            title={complex.title}
            transports={complex.transports}
            url={complex.publicUrl}
          />,
        ),
      ),
    };
  };

  useEffect(() => {
    setPinTypes(filteredComplexes.map(complex => String(complex.id)));
  }, [filteredComplexes]);

  useEffect(() => {
    const onDocumentClickCapture = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      const el = target?.closest?.('[data-pik-map-balloon-cta]');

      if (!el) {
        return;
      }

      const id = el.getAttribute('data-newbuilding-id');

      if (id) {
        trackMapBalloonCtaClick(id);
      }
    };

    document.addEventListener('click', onDocumentClickCapture, true);

    return (): void => {
      document.removeEventListener('click', onDocumentClickCapture, true);
    };
  }, []);

  return (
    <Map
      apiKey={mapApiKey}
      currentTypes={pinTypes}
      pinCoordinates={center}
      zoom={zoom}
      onBalloonClose={handleBalloonClose}
      onBalloonOpen={handleBalloonOpen}
      onCustomBalloonHtmlFetch={handleBalloonHtmlFirstFetch}
      onGeoObjectClick={handleGeoObjectClick}
      onGeoObjectsFirstFetch={handleGeoObjectsFirstFetch}
    />
  );
};
